import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

// Map backend redirect_to values to frontend routes
const REDIRECT_ROUTE_MAP = {
  'PAYMENT': '/payment',
  'INTERVIEWEE_DASHBOARD': '/dashboard',
  'COMPANY_DASHBOARD': '/company',
  'COLLEGE_DASHBOARD': '/college',
  'ADMIN_DASHBOARD': '/admin/analytics',
};

const VALID_REDIRECTS = Object.keys(REDIRECT_ROUTE_MAP);

export function AuthProvider({ children }) {
  const apiBase = process.env.REACT_APP_API_BASE || "http://localhost:8000";

  // Catch the common mistake of deploying without REACT_APP_API_BASE set
  if (process.env.NODE_ENV === 'production' && apiBase.includes('localhost')) {
    console.error(
      '⚠️ REACT_APP_API_BASE is pointing to localhost in a production build. ' +
      'Set REACT_APP_API_BASE=https://mockbot-api1.onrender.com in your Render dashboard and redeploy.'
    );
  }
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [backendRedirectTo, setBackendRedirectTo] = useState(null);
  const [interviewConfig, setInterviewConfig] = useState(() => {
    const saved = localStorage.getItem('interviewConfig');
    return saved ? JSON.parse(saved) : null;
  });
  const [sessionId, setSessionId] = useState(null);
  const [mountKey, setMountKey] = useState(0);

  // ========== Auth state listener — recovers session on page refresh ==========
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        console.log('🔄 Auth state: User logged in, fetching backend redirect...');
        try {
          const token = await currentUser.getIdToken();
          const response = await fetch(`${apiBase}/auth/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              firebase_uid: currentUser.uid,
              email: currentUser.email,
              display_name: currentUser.displayName || '',
              photo_url: currentUser.photoURL || '',
              provider: 'refresh'
            })
          });

          if (response.ok) {
            const data = await response.json();
            console.log('✅ Backend redirect fetched:', data.redirect_to);
            setBackendRedirectTo(data.redirect_to);
          } else {
            console.error('❌ Failed to fetch backend redirect');
            setBackendRedirectTo(null);
          }
        } catch (error) {
          console.error('❌ Error fetching backend redirect:', error);
          setBackendRedirectTo(null);
        }
      } else {
        console.log('🔄 Auth state: No user logged in');
        setBackendRedirectTo(null);
      }

      setUser(currentUser);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [apiBase]);

  // ========== Media cleanup when interview config is cleared ==========
  useEffect(() => {
    if (!interviewConfig) {
      if (window.SpeechRecognition || window.webkitSpeechRecognition) {
        try {
          // Force stop any active recognition
        } catch (e) {
          console.log('Error stopping recognition:', e);
        }
      }

      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        try { audio.pause(); audio.currentTime = 0; } catch (e) {}
      });

      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (window.pendingAudioData) delete window.pendingAudioData;
    }
  }, [interviewConfig]);

  // ========== Route helper ==========
  const getRouteForRedirect = useCallback((redirect) => {
    return REDIRECT_ROUTE_MAP[redirect] || '/login';
  }, []);

  // ========== Login handler — called by LoginScreen after Firebase auth + backend registration ==========
  const handleLogin = useCallback((loginData) => {
    console.log('🔐 User logged in, backend navigation:', loginData.redirectTo);

    if (!loginData.redirectTo) {
      console.error('❌ SECURITY: Backend did not provide redirect_to field');
      toast.error('Authentication error: Backend did not specify next page. Please try logging in again.');
      signOut(auth);
      setUser(null);
      setBackendRedirectTo(null);
      navigate('/login');
      return;
    }

    if (!VALID_REDIRECTS.includes(loginData.redirectTo)) {
      console.error('❌ SECURITY: Invalid redirect_to value:', loginData.redirectTo);
      toast.error('Authentication error: Invalid redirect specified. Please try logging in again.');
      signOut(auth);
      setUser(null);
      setBackendRedirectTo(null);
      navigate('/login');
      return;
    }

    setUser(loginData.user);
    setBackendRedirectTo(loginData.redirectTo);
    const returnTo = typeof loginData.returnTo === 'string' &&
      (loginData.returnTo.startsWith('/interview/') || loginData.returnTo.startsWith('/inst/') || loginData.returnTo === '/setup')
      ? loginData.returnTo
      : null;
    navigate(returnTo || REDIRECT_ROUTE_MAP[loginData.redirectTo]);
  }, [navigate]);

  // ========== Logout handler ==========
  const handleLogout = useCallback(async () => {
    try {
      // Stop interview session on backend BEFORE signOut
      try {
        const sid = sessionStorage.getItem('mockbot_session_id');
        if (sid && user) {
          const idToken = await user.getIdToken();
          console.log("🛑 Cleanup: Stopping interview session before logout:", sid);
          try {
            await fetch(`${apiBase}/stop_interview`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
              body: JSON.stringify({ session_id: sid })
            });
            console.log("✅ Interview session stopped successfully");
          } catch (stopError) {
            console.error("⚠️ Error stopping interview (continuing logout):", stopError);
          }
        }
      } catch (error) {
        console.error("Error stopping interview session:", error);
      }

      // Clear media
      try {
        document.querySelectorAll('audio').forEach(audio => {
          try { audio.pause(); audio.currentTime = 0; } catch (e) {}
        });
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        console.log("✅ Cleanup: All media resources cleared");
      } catch (error) {
        console.error("Error clearing media resources:", error);
      }

      console.log("🔓 Logout: Clearing authentication state");
      await signOut(auth);

      setUser(null);
      setBackendRedirectTo(null);
      setInterviewConfig(null);
      setSessionId(null);
      localStorage.removeItem('interviewConfig');
      sessionStorage.removeItem('mockbot_session_id');

      console.log("🔑 Logout complete: Returning to login screen");
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      setUser(null);
      setBackendRedirectTo(null);
      setInterviewConfig(null);
      setSessionId(null);
      localStorage.removeItem('interviewConfig');
      sessionStorage.removeItem('mockbot_session_id');
      navigate('/login');
    }
  }, [apiBase, user, navigate]);

  // ========== Go back from interview to dashboard ==========
  const handleGoBack = useCallback(async () => {
    try {
      const sid = sessionStorage.getItem('mockbot_session_id');
      if (sid && user) {
        const idToken = await user.getIdToken();
        console.log("🛑 Stopping interview session:", sid);
        await fetch(`${apiBase}/stop_interview`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
          body: JSON.stringify({ session_id: sid })
        }).catch(err => console.error("Error stopping interview:", err));
      }
    } catch (error) {
      console.error("Error stopping interview:", error);
    }

    setInterviewConfig(null);
    setSessionId(null);
    localStorage.removeItem('interviewConfig');
    navigate('/dashboard');
  }, [apiBase, user, navigate]);

  // ========== Start interview — called from SetupScreen ==========
  const handleStartInterview = useCallback(async (config) => {
    setInterviewConfig(config);
    localStorage.setItem('interviewConfig', JSON.stringify(config));
    setMountKey(prev => prev + 1);
    navigate('/interview');
  }, [navigate]);

  // ========== Payment callbacks ==========
  const handlePaymentSuccess = useCallback((response) => {
    console.log("✅ Payment successful, navigating to dashboard");
    setInterviewConfig(null);
    localStorage.removeItem('interviewConfig');
    setBackendRedirectTo('INTERVIEWEE_DASHBOARD');
    navigate('/dashboard');
  }, [navigate]);

  const handlePaymentBack = useCallback(() => {
    console.log("🔄 Returning to login from payment");
    signOut(auth);
    setUser(null);
    setBackendRedirectTo(null);
    navigate('/login');
  }, [navigate]);

  const value = {
    apiBase,
    user,
    authLoading,
    backendRedirectTo, setBackendRedirectTo,
    interviewConfig, setInterviewConfig,
    sessionId, setSessionId,
    mountKey,
    handleLogin,
    handleLogout,
    handleGoBack,
    handleStartInterview,
    handlePaymentSuccess,
    handlePaymentBack,
    getRouteForRedirect
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
